import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { galleryItems, galleryCategories } from "../../mocks/gallery";
import { Link } from "react-router-dom";

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightbox, setLightbox] = useState<null | typeof galleryItems[0]>(null);

  const filtered =
    activeCategory === "All"
      ? galleryItems
      : galleryItems.filter((g) => g.category === activeCategory);

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/00204ca5a393998b319b708458418844.jpg"
            alt="Gallery"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-zinc-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Our Portfolio</span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mt-4 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Project Gallery
          </h1>
          <p className="text-stone-300 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Browse a selection of our completed installations across Surrey and South London. Every project tells its own story.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-2 justify-center">
          {galleryCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-amber-600 text-white"
                  : "bg-stone-100 text-zinc-600 hover:bg-stone-200"
              }`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <button
                key={item.id}
                onClick={() => setLightbox(item)}
                className="group relative rounded-lg overflow-hidden cursor-pointer text-left w-full"
              >
                <div className="w-full h-64">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-12 h-12 flex items-center justify-center bg-white rounded-full">
                    <i className="ri-zoom-in-line text-zinc-800 text-xl"></i>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <span className="bg-amber-600 text-white text-xs font-bold px-2 py-0.5 rounded mb-2 inline-block" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.category}</span>
                  <h3 className="text-white text-base font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.title}</h3>
                  <p className="text-stone-300 text-xs flex items-center gap-1" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    <i className="ri-map-pin-2-line text-amber-400 text-xs"></i>
                    {item.location}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-zinc-400" style={{ fontFamily: "'Raleway', sans-serif" }}>
              No projects found for this category yet.
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Love What You See?</h3>
          <p className="text-amber-100 mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>Let us transform your home. Get in touch for a free, no-obligation quote today.</p>
          <Link to="/contact" className="bg-white text-amber-600 hover:bg-stone-50 font-semibold px-8 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Get a Free Quote
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-12 right-0 text-white text-2xl cursor-pointer w-10 h-10 flex items-center justify-center"
            >
              <i className="ri-close-line"></i>
            </button>
            <div className="w-full h-[500px] rounded-lg overflow-hidden">
              <img src={lightbox.image} alt={lightbox.title} className="w-full h-full object-cover object-top" />
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{lightbox.title}</h3>
              <p className="text-stone-300 text-sm mt-1" style={{ fontFamily: "'Raleway', sans-serif" }}>
                <i className="ri-map-pin-2-line text-amber-400 mr-1"></i>{lightbox.location} · {lightbox.category}
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
}
